import { useMemo, useState } from 'react'
import { Check, Plus, Search, X } from 'lucide-react'
import { plural } from './plural'
import type { RecipeCardOption } from './RecipesTab'
import { useOverlayBack } from './overlayBack'

interface RecipeLibrarySheetProps {
  /** Все рецепты, у которых есть калькулятор. Список общий для всех. */
  cards: readonly RecipeCardOption[]
  /** Что уже добавлено себе. */
  savedIds: ReadonlySet<number>
  onToggle: (cardId: number, next: boolean) => void
  onClose: () => void
}

/**
 * Каталог рецептов с калькулятором: отсюда человек набирает себе те, по которым
 * считает. Список общий и длинный, поэтому с поиском.
 */
const RecipeLibrarySheet = ({
  cards,
  savedIds,
  onToggle,
  onClose,
}: RecipeLibrarySheetProps) => {
  useOverlayBack(onClose)
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return cards
    return cards.filter(card => card.title.toLowerCase().includes(needle))
  }, [cards, query])

  return (
    <div className="cost-sheet" role="dialog" aria-modal="true">
      <div className="cost-sheet__head">
        <h2>Добавить рецепты</h2>
        <button type="button" onClick={onClose} aria-label="Закрыть">
          <X size={20} />
        </button>
      </div>

      <p className="cost-sheet__hint">
        Отмечайте рецепты, по которым считаете, — они появятся в «Моих
        рецептах». Отметить можно сколько угодно.
      </p>

      <label className="cost-sheet__search">
        <Search size={18} aria-hidden />
        <input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Поиск по названию"
        />
      </label>

      <div className="cost-sheet__list">
        {filtered.map(card => {
          const isSaved = savedIds.has(card.cardId)
          return (
            <button
              key={card.cardId}
              type="button"
              className={`cost-sheet__item cost-library__item ${isSaved ? 'active' : ''}`}
              onClick={() => onToggle(card.cardId, !isSaved)}
            >
              {card.icon ? (
                <img
                  src={card.icon}
                  alt=""
                  loading="lazy"
                  className="cost-library__cover"
                />
              ) : (
                <span className="cost-library__cover" />
              )}
              <span className="cost-library__title">{card.title}</span>
              <span
                className={`cost-library__mark ${isSaved ? 'active' : ''}`}
                aria-hidden
              >
                {isSaved ? <Check size={16} /> : <Plus size={16} />}
              </span>
            </button>
          )
        })}

        {filtered.length === 0 && (
          <p className="cost-sheet__empty">
            {query.trim()
              ? `По запросу «${query}» рецептов не нашлось`
              : 'Рецептов с калькулятором пока нет'}
          </p>
        )}
      </div>

      <div className="cost-sheet__footer">
        <button type="button" className="cost-button wide" onClick={onClose}>
          {savedIds.size === 0
            ? 'Готово'
            : `Готово · ${savedIds.size} ${plural(savedIds.size, 'рецепт', 'рецепта', 'рецептов')}`}
        </button>
      </div>
    </div>
  )
}

export default RecipeLibrarySheet
